// src/modules/pedidos/pages/RecuentosLista.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const ESTADO_CFG = {
  ASIGNADO:   { label: "Asignado",            color: "#b45309", bg: "#fef3c7" },
  EN_CURSO:   { label: "Realizando recuento", color: "#1d4ed8", bg: "#dbeafe" },
  TERMINADO:  { label: "Recuento terminado",  color: "#7c3aed", bg: "#f3e8ff" },
  CONFIRMADO: { label: "Confirmado",          color: "#15803d", bg: "#dcfce7" },
};

function fmt(ts) {
  if (!ts?.toDate) return "";
  return ts.toDate().toLocaleString("es-UY", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function RecuentosLista() {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const rol = (profile?.rol || "").toLowerCase();
  const esOperario = rol === "operario";
  const esVentas = rol === "ventas";
  const esAdmin = rol === "admin";
  const puedeCrear = esAdmin || rol === "encargado";
  const dep = profile?.deposito || null;

  const [recuentos, setRecuentos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const qy = query(collection(db, "recuentos"), orderBy("creadoAt", "desc"));
    const unsub = onSnapshot(qy, snap => {
      setRecuentos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => { console.error("[recuentos]", err); setLoading(false); });
    return () => unsub();
  }, []);

  const visibles = useMemo(() => {
    return recuentos.filter(r => {
      // Operario: solo los asignados a él (sin importar depósito)
      if (esOperario) return r.asignadoAUid === user?.uid;
      // Encargado: su depósito · Admin: todos
      if (!esAdmin && dep && r.deposito && r.deposito !== dep) return false;
      return true;
    });
  }, [recuentos, esAdmin, esOperario, dep, user?.uid]);

  const activos = visibles.filter(r => r.estado !== "CONFIRMADO");
  const cerrados = visibles.filter(r => r.estado === "CONFIRMADO");

  const Card = ({ r }) => {
    const cfg = ESTADO_CFG[r.estado] || ESTADO_CFG.ASIGNADO;
    const prog = r.totalProductos ? Math.round((r.contados / r.totalProductos) * 100) : 0;
    return (
      <button onClick={() => navigate(`/recuentos/${r.id}`)} className="card"
        style={{ width: "100%", textAlign: "left", padding: "13px 15px", cursor: "pointer", border: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
          <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#0f172a", flex: 1 }}>{r.nombre}</span>
          <span style={{ fontSize: "0.66rem", fontWeight: 700, color: cfg.color, background: cfg.bg, padding: "2px 9px", borderRadius: "999px" }}>{cfg.label}</span>
        </div>
        {r.motivo && <p style={{ margin: "0 0 4px", fontSize: "0.8rem", color: "#64748b" }}>{r.motivo}</p>}
        <div style={{ display: "flex", gap: "10px", fontSize: "0.72rem", color: "#94a3b8", flexWrap: "wrap" }}>
          {r.deposito && <span>🏭 {r.deposito}</span>}
          <span>📦 {r.contados}/{r.totalProductos} contados</span>
          {r.asignadoANombre && <span>👤 {r.asignadoANombre}</span>}
          <span>🕐 {fmt(r.creadoAt)}</span>
        </div>
        {(r.estado === "ASIGNADO" || r.estado === "EN_CURSO") && (
          <div style={{ marginTop: "7px", height: "5px", background: "#f1f5f9", borderRadius: "999px", overflow: "hidden" }}>
            <div style={{ width: `${prog}%`, height: "100%", background: cfg.color }} />
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="container" style={{ paddingBottom: "40px", maxWidth: "720px" }}>
      <header className="topbar card" style={{ marginBottom: "16px" }}>
        <button onClick={() => navigate("/")} className="btn btn--ghost btn-sm">⬅ Volver</button>
        <div>
          <h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>📦 Recuentos de stock</h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>{esOperario ? "Asignados a vos" : (dep && !esAdmin ? dep : "Todos")}</p>
        </div>
        {puedeCrear && (
          <button onClick={() => navigate("/recuentos/nuevo")} className="btn btn--primary btn-sm">+ Nuevo</button>
        )}
      </header>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>Cargando…</div>
      ) : visibles.length === 0 ? (
        <div style={{ textAlign: "center", padding: "64px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📦</div>
          <strong style={{ color: "#334155", display: "block", fontSize: "16px", marginBottom: "6px" }}>No hay recuentos</strong>
          {!esOperario && <span style={{ fontSize: "13px" }}>Tocá <strong>+ Nuevo</strong> para crear uno</span>}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {activos.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {activos.map(r => <Card key={r.id} r={r} />)}
            </div>
          )}
          {cerrados.length > 0 && (
            <div>
              <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Confirmados</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {cerrados.map(r => <Card key={r.id} r={r} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
